import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Flame, Trophy, X } from "lucide-react";
import { useData } from "../context/DataContext";
import { randomInt } from "../engine/random";
import { burst } from "../lib/fx";
import { sfx } from "../lib/sound";
import { PopButton } from "./PopButton";

const BEST_KEY = "dither.guessBest";
const SPIN_MS = 2000;
const angleFor = (n: number) => -120 + ((n - 1) * 240) / 9;

const CX = 100, CY = 82, R = 72;
const pt = (deg: number, r: number) => { const a = ((deg - 90) * Math.PI) / 180; return [CX + r * Math.cos(a), CY + r * Math.sin(a)]; };

function Speedometer({ angle, swing }: { angle: number; swing: number[] | null }) {
  const [sx, sy] = pt(-120, R), [ex, ey] = pt(120, R);
  const arc = `M ${sx} ${sy} A ${R} ${R} 0 1 1 ${ex} ${ey}`;
  return (
    <svg viewBox="0 0 200 132" className="mx-auto block h-36 w-60" data-testid="guess-speedometer">
      <defs><linearGradient id="guess-g"><stop offset="0" stopColor="#35E07A" /><stop offset="0.5" stopColor="#FFE14D" /><stop offset="1" stopColor="#FF5D1F" /></linearGradient></defs>
      <path d={arc} fill="none" stroke="#3b1800" strokeWidth="14" strokeLinecap="round" />
      <path d={arc} fill="none" stroke="url(#guess-g)" strokeWidth="9" strokeLinecap="round" />
      {Array.from({ length: 10 }, (_, i) => {
        const [x, y] = pt(angleFor(i + 1), R - 22);
        return <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="central" className="font-comic" fontSize="14" fill="#3b1800">{i + 1}</text>;
      })}
      <motion.g initial={false} animate={{ rotate: swing ?? angle }} style={{ originX: "50%", originY: "50%" }}
        transition={swing ? { duration: SPIN_MS / 1000, ease: "easeOut", times: swing.map((_, i) => i / (swing.length - 1)) } : { duration: 0.2 }}>
        <circle cx={CX} cy={CY} r={R} fill="transparent" />
        <line x1={CX} y1={CY} x2={CX} y2={CY - R + 30} stroke="#C8102E" strokeWidth="6" strokeLinecap="round" />
      </motion.g>
      <circle cx={CX} cy={CY} r="8" fill="#FFE14D" stroke="#3b1800" strokeWidth="3" />
    </svg>
  );
}

function useGuessGame() {
  const { prefs } = useData();
  const [streak, setStreak] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem(BEST_KEY) || 0));
  const [angle, setAngle] = useState(0);
  const [swing, setSwing] = useState<number[] | null>(null);
  const [outcome, setOutcome] = useState<{ ok: boolean; target: number; guess: number } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const play = (guess: number) => {
    const target = randomInt(1, 10);
    const final = angleFor(target);
    setOutcome(null);
    setSwing([angle, ...Array.from({ length: 7 }, () => angleFor(randomInt(1, 10))), final]);
    if (prefs.sound_effects) sfx.spin(SPIN_MS / 1000);
    timer.current = window.setTimeout(() => {
      setSwing(null);
      setAngle(final);
      const ok = guess === target;
      setOutcome({ ok, target, guess });
      if (ok) {
        const s = streak + 1;
        setStreak(s);
        if (s > best) { setBest(s); localStorage.setItem(BEST_KEY, String(s)); }
        burst();
        if (prefs.sound_effects) sfx.fanfare();
      } else {
        setStreak(0);
        if (prefs.sound_effects) sfx.buzzer();
      }
    }, SPIN_MS);
  };
  return { streak, best, angle, swing, outcome, play, spinning: !!swing };
}

function GuessGame() {
  const g = useGuessGame();
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { if (!g.spinning) input.current?.focus(); }, [g.spinning]);
  const go = () => {
    const n = Number(value);
    if (!Number.isInteger(n) || n < 1 || n > 10) return setError("Enter a whole number from 1 to 10.");
    setError("");
    setValue("");
    g.play(n);
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-center gap-2 text-sm font-extrabold uppercase" data-testid="guess-streak-bar">
        <span className="flex items-center gap-1 rounded-full border-2 border-[#3b1800] bg-[#FF5D1F] px-3 py-1 text-white"><Flame className="h-4 w-4" strokeWidth={3} /> Streak: <span data-testid="guess-streak">{g.streak}</span></span>
        <span className="flex items-center gap-1 rounded-full border-2 border-[#3b1800] bg-[#FFE14D] px-3 py-1 text-[#3b1800]"><Trophy className="h-4 w-4" strokeWidth={3} /> Best: <span data-testid="guess-best">{g.best}</span></span>
      </div>
      <p className="text-center font-comic text-lg text-[#3b1800]" data-testid="guess-prompt">Pick a number between 1 and 10</p>
      <Speedometer angle={g.angle} swing={g.swing} />
      <div className="h-12 text-center" aria-live="polite">
        {g.spinning && <p className="font-comic text-lg text-[#3b1800]/70" data-testid="guess-spinning">Spinning…</p>}
        {g.outcome && (
          <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} data-testid="guess-result">
            <p className={`ua-title text-3xl ${g.outcome.ok ? "text-[#0E7A3E]" : "text-[#C8102E]"}`} data-testid={g.outcome.ok ? "guess-correct" : "guess-wrong"}>{g.outcome.ok ? "Correct!" : `Unlucky — it was ${g.outcome.target}`}</p>
            <p className="text-xs font-bold text-[#3b1800]/70">{g.outcome.ok ? `You guessed ${g.outcome.guess}. Keep it going!` : `You guessed ${g.outcome.guess}. Streak reset — go again!`}</p>
          </motion.div>
        )}
      </div>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (!g.spinning) go(); }}>
        <input ref={input} className="field text-center text-xl" inputMode="numeric" maxLength={2} placeholder="1–10" value={value} disabled={g.spinning} onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))} data-testid="guess-input" aria-label="Your guess" />
        <PopButton type="submit" tone="green" size="lg" disabled={g.spinning || !value} data-testid="guess-go-button">Go</PopButton>
      </form>
      {error && <p className="text-center text-sm font-bold text-[#C8102E]" data-testid="guess-error">{error}</p>}
    </div>
  );
}

export function PickNumberPopup({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-[#2a1300]/70 px-3 backdrop-blur-md" onClick={onClose} data-testid="pick-number-popup">
      <div className="relative w-full max-w-sm rounded-[26px] border-[4px] border-[#FFC15E] bg-[#FFF3D6] p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" data-testid="pick-number-popup-close" className="absolute right-3 top-3 rounded-full border-2 border-ink p-1 hover:bg-pop-yellow"><X className="h-4 w-4" strokeWidth={3} /></button>
        <h2 className="ua-title mb-3 text-3xl text-[#FF5D1F]" data-testid="pick-number-popup-title">I'm bored</h2>
        <GuessGame />
      </div>
    </div>,
    document.body,
  );
}
